(function () {
  'use strict';
  const MAX = 5;
  function quantity(value, fallback) {
    if (value === undefined || value === null || value === '') return fallback;
    const number = Number(value);
    if (!Number.isSafeInteger(number) || number < 0) throw new Error('Invalid quantity');
    return number;
  }
  function projectTotal(cart, action, data) {
    const items = cart.items || [];
    const total = items.reduce((sum, item) => sum + item.quantity, 0);
    if (action === 'clear') return 0;
    if (action === 'add') {
      const additions = data.items || [data];
      return total + additions.reduce((sum, item) => sum + quantity(item.quantity, 1), 0);
    }
    if (action === 'change') {
      const index = data.line !== undefined
        ? quantity(data.line, 0) - 1
        : items.findIndex(item => item.key === String(data.id) || String(item.variant_id || item.id) === String(data.id));
      if (index < 0 || index >= items.length) throw new Error('Unknown cart line');
      return total - items[index].quantity + quantity(data.quantity, 1);
    }
    if (action === 'update' && data.updates) {
      if (Array.isArray(data.updates)) {
        return items.reduce((sum, item, i) => sum + quantity(data.updates[i], item.quantity), 0);
      }
      let result = total;
      for (const [key, value] of Object.entries(data.updates)) {
        const matches = items.filter(item => item.key === key || String(item.variant_id || item.id) === key);
        if (matches.length) {
          for (const item of matches) result += quantity(value, item.quantity) - item.quantity;
        } else {
          result += quantity(value, 0);
        }
      }
      return result;
    }
    return total;
  }
  function decodeEntries(entries) {
    const data = {};
    const items = [];
    for (const [name, value] of entries) {
      const item = name.match(/^items\[(\d+)\]\[(id|quantity)\]$/);
      const update = name.match(/^updates\[(.*?)\]$/);
      if (item) {
        items[Number(item[1])] ||= {};
        items[Number(item[1])][item[2]] = value;
      } else if (update) {
        data.updates ||= update[1] === '' ? [] : {};
        if (update[1] === '') data.updates.push(value);
        else data.updates[update[1]] = value;
      } else if (['id', 'quantity', 'line'].includes(name)) data[name] = value;
    }
    if (items.length) data.items = items.filter(Boolean);
    return data;
  }
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { projectTotal, decodeEntries };
    return;
  }
  const config = window.chiffonOrderLimit;
  if (!config || window.chiffonOrderLimitInstalled) return;
  window.chiffonOrderLimitInstalled = true;
  const originalFetch = window.fetch.bind(window);
  let queue = Promise.resolve();
  const permittedForms = new WeakSet();
  const root = config.root || '/';
  const message = config.message;
  function announce(text) {
    let notice = document.getElementById('ChiffonOrderLimitAlert');
    if (!notice) {
      notice = document.createElement('div');
      notice.id = 'ChiffonOrderLimitAlert';
      notice.setAttribute('role', 'alert');
      notice.style.cssText = 'position:fixed;bottom:20px;left:5%;right:5%;max-width:620px;margin:auto;padding:16px 20px;background:#321004;color:#fff;border-radius:4px;z-index:100000;text-align:center;box-shadow:0 3px 18px #0004';
      document.body.appendChild(notice);
    }
    notice.hidden = false;
    notice.textContent = text;
    clearTimeout(announce.timer);
    announce.timer = setTimeout(() => { notice.hidden = true; }, 8000);
  }
  async function readCart() {
    const response = await originalFetch(root + 'cart.js?t=' + Date.now(), { credentials: 'same-origin', cache: 'no-store' });
    if (!response.ok) throw new Error('Cart unavailable');
    return response.json();
  }
  function schedule(task) {
    const next = queue.then(task);
    queue = next.catch(() => {});
    return next;
  }
  function errorResponse(text) {
    return new Response(JSON.stringify({ status: 422, message: text, description: text }), {
      status: 422, headers: { 'Content-Type': 'application/json' }
    });
  }
  async function bodyData(input, options) {
    let body = options && options.body;
    if (body === undefined && input instanceof Request) body = await input.clone().text();
    if (!body) return {};
    if (body instanceof FormData || body instanceof URLSearchParams) return decodeEntries(body.entries());
    if (typeof body !== 'string') throw new Error('Unsupported cart request');
    if (body.trim().startsWith('{')) return JSON.parse(body);
    return decodeEntries(new URLSearchParams(body).entries());
  }
  window.fetch = function (input, options) {
    const url = new URL(input instanceof Request ? input.url : String(input), location.href);
    const action = url.pathname.match(/\/cart\/(add|change|update|clear)(?:\.js)?\/?$/);
    const method = String((options && options.method) || (input instanceof Request ? input.method : 'GET')).toUpperCase();
    if (url.origin !== location.origin || !action || method !== 'POST') return originalFetch(input, options);
    return schedule(async () => {
      let cart;
      try {
        const data = await bodyData(input, options);
        if (action[1] === 'clear' || (action[1] === 'update' && !data.updates)) return originalFetch(input, options);
        cart = await readCart();
        const current = cart.items.reduce((sum, item) => sum + item.quantity, 0);
        const projected = projectTotal(cart, action[1], data);
        if (projected > MAX && projected >= current) {
          if (action[1] === 'add') announce(message);
          return errorResponse(message);
        }
      } catch (error) {
        announce(config.retryMessage);
        return errorResponse(config.retryMessage);
      }
      return originalFetch(input, options);
    });
  };
  async function approveCheckout(form) {
    await queue;
    const cart = await readCart();
    const count = cart.items.reduce((sum, item) => sum + item.quantity, 0);
    const inputs = form ? [...form.querySelectorAll('[name="updates[]"]')] : [];
    const requested = inputs.length ? inputs.reduce((sum, input) => sum + quantity(input.value, 0), 0) : count;
    if (count > MAX || requested > MAX) { announce(message); return false; }
    return true;
  }
  document.addEventListener('submit', async event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const url = new URL(form.action, location.href);
    const isCart = /\/cart\/?$/.test(url.pathname);
    const isCheckout = /\/checkout\/?$/.test(url.pathname) || (isCart && (event.submitter?.name === 'checkout' || !event.submitter));
    if (!isCheckout) return;
    if (permittedForms.has(form)) { permittedForms.delete(form); return; }
    event.preventDefault();
    event.stopImmediatePropagation();
    try {
      if (await approveCheckout(form)) {
        permittedForms.add(form);
        form.requestSubmit(event.submitter || undefined);
      }
    } catch (error) { announce(config.retryMessage); }
  }, true);
  document.addEventListener('submit', async event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || event.defaultPrevented) return;
    if (permittedForms.has(form)) { permittedForms.delete(form); return; }
    const path = new URL(form.action, location.href).pathname;
    const add = /\/cart\/add(?:\.js)?\/?$/.test(path);
    const update = /\/cart\/?$/.test(path);
    if (update && (event.submitter?.name === 'checkout' || !event.submitter)) return;
    if (!add && !update) return;
    event.preventDefault();
    try {
      await queue;
      const cart = await readCart();
      const data = decodeEntries(new FormData(form).entries());
      const current = cart.items.reduce((sum, item) => sum + item.quantity, 0);
      const next = projectTotal(cart, add ? 'add' : 'update', data);
      if (next > MAX && next >= current) { announce(message); return; }
      permittedForms.add(form);
      form.requestSubmit(event.submitter || undefined);
    } catch (error) { announce(config.retryMessage); }
  });
  document.addEventListener('click', async event => {
    const link = event.target.closest('a[href]');
    if (!link) return;
    const url = new URL(link.href, location.href);
    if (url.origin !== location.origin || !/\/checkout\/?$/.test(url.pathname)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    try { if (await approveCheckout(null)) location.assign(link.href); }
    catch (error) { announce(config.retryMessage); }
  }, true);
}());
